import React, { useState, useEffect } from 'react';
import { subscribeHrAdminEmails, addHrAdminEmail, removeHrAdminEmail } from '../services/hrBootstrapService';
import { HR_ADMIN_EMAILS } from '../config/hrBootstrap';
import { Plus, Trash2, Shield } from 'lucide-react';

export default function AdminAccessSettings({ showToast }) {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsub = subscribeHrAdminEmails(setEmails);
    return () => unsub();
  }, []);

  const persistAdd = async () => {
    const t = newEmail.trim().toLowerCase();
    if (!t) return;
    setSaving(true);
    try {
      await addHrAdminEmail(t);
      setNewEmail('');
      showToast('Admin access saved. User can sign in with Google to get HR access.', 'ok');
    } catch (e) {
      showToast(e.message || 'Could not save', 'err');
    } finally {
      setSaving(false);
    }
  };

  const persistRemove = async (email) => {
    if (!window.confirm(`Remove HR admin access for ${email}? They will not auto-receive HR on next login if not in the built-in list.`)) return;
    setSaving(true);
    try {
      await removeHrAdminEmail(email);
      showToast('Removed from Admin access list', 'inf');
    } catch (e) {
      showToast(e.message || 'Could not remove', 'err');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pg">
      <div className="ph" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              background: 'var(--accent-bg)',
              color: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Shield size={22} />
          </div>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700' }}>Admin access</h1>
            <p style={{ color: 'var(--text2)', fontSize: '13px', marginTop: '4px' }}>
              Add Google accounts that should get <strong>HR (admin)</strong> access on first sign-in. Deploy updated{' '}
              <code style={{ fontSize: '12px' }}>firestore.rules</code> after pulling this feature.
            </p>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: '20px', maxWidth: '560px' }}>
        <p style={{ fontSize: '12px', color: 'var(--text3)', marginBottom: '16px', lineHeight: 1.5 }}>
          Built-in bootstrap emails (from code) always work: {HR_ADMIN_EMAILS.join(', ')} and <em>@techacadxl.com</em> domain.
        </p>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), persistAdd())}
            placeholder="email@company.com"
            disabled={saving}
            style={{
              flex: '1 1 220px',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              fontSize: '14px',
            }}
          />
          <button
            type="button"
            className="btn btn-p"
            onClick={persistAdd}
            disabled={saving || !newEmail.trim()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <Plus size={18} /> Add HR admin
          </button>
        </div>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {emails.length === 0 ? (
            <li style={{ fontSize: '13px', color: 'var(--text3)', padding: '8px 0' }}>
              No extra admin emails yet — only built-in / domain rules apply.
            </li>
          ) : (
            emails.map((em) => (
              <li
                key={em}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 14px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                <span>{em}</span>
                <button
                  type="button"
                  className="btn"
                  onClick={() => persistRemove(em)}
                  disabled={saving}
                  style={{
                    padding: '6px 10px',
                    color: 'var(--red, #c0392b)',
                    border: '1px solid var(--border)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                  title="Remove"
                >
                  <Trash2 size={16} />
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
